import type { CommunicationTemplate } from "./communicationTemplates";
import {
  COMMUNICATION_TEMPLATES,
  DEEP_LINK_PROTOCOLS,
  findTemplateByProtocol,
} from "./communicationTemplates";
import { isValidUrl, extractProtocol, isDeepLink } from "./deepLinkHelpers";

export const extractValueFromDeepLink = (
  builtLink: string,
  template: CommunicationTemplate
): string => {
  try {
    const url = new URL(builtLink);

    switch (template.id) {
      case "whatsapp": {
        const phone = url.searchParams.get("phone");
        return phone ? `+${phone}` : "";
      }
      case "telegram": {
        const domain = url.searchParams.get("domain");
        return domain || "";
      }
      case "phone":
      case "sms": {
        return url.pathname || "";
      }
      case "email": {
        return builtLink.replace("mailto:", "");
      }
      case "linkedin": {
        const pathMatch = builtLink.match(
          /linkedin:\/\/(profile|company)\/(.+)/
        );
        return pathMatch ? decodeURIComponent(pathMatch[2]) : "";
      }
      case "twitter": {
        const handle = url.searchParams.get("screen_name");
        return handle || "";
      }
      case "instagram": {
        const username = url.searchParams.get("username");
        return username || "";
      }
      case "facebook": {
        const href = url.searchParams.get("href");
        if (href) {
          try {
            const fbUrl = new URL(decodeURIComponent(href));
            return fbUrl.pathname.replace(/^\//, "");
          } catch {
            return href;
          }
        }
        return "";
      }
      case "maps": {
        const query = url.searchParams.get("q");
        return query ? decodeURIComponent(query) : "";
      }
      case "youtube": {
        const videoId = url.searchParams.get("v");
        if (videoId) {
          return `watch?v=${videoId}`;
        }
        const pathname = url.pathname;
        if (pathname.includes("youtube.com/")) {
          const pathAfterDomain = pathname.split("youtube.com/")[1];
          return pathAfterDomain || "";
        }
        return pathname.replace(/^\/+/, "");
      }
      default: {
        return url.pathname.replace(/^\/+/, "");
      }
    }
  } catch (error) {
    console.warn(`Failed to extract value from deep link: ${builtLink}`, error);
    return "";
  }
};

export const validateLink = (
  link: string,
  template?: CommunicationTemplate,
  allowedProtocols: string[] = ["https", "http"],
  templates: CommunicationTemplate[] = COMMUNICATION_TEMPLATES
): { isValid: boolean; error?: string } => {
  if (!link || !link.trim()) {
    return { isValid: false, error: "Link is required" };
  }

  const trimmedLink = link.trim();
  const protocol = extractProtocol(trimmedLink);
  const resolvedTemplate =
    template ??
    (protocol ? findTemplateByProtocol(protocol, templates) : undefined);

  if (resolvedTemplate) {
    if (protocol) {
      const matchesTemplate = Boolean(
        findTemplateByProtocol(protocol, [resolvedTemplate])
      );
      if (!matchesTemplate) {
        return {
          isValid: false,
          error: `Expected ${resolvedTemplate.label} link`,
        };
      }
    }

    if (resolvedTemplate.validator) {
      try {
        const value = extractValueFromDeepLink(trimmedLink, resolvedTemplate);
        if (!resolvedTemplate.validator(value)) {
          return {
            isValid: false,
            error: `Invalid ${resolvedTemplate.inputLabel.toLowerCase()}`,
          };
        }
      } catch (error) {
        console.warn(`Failed to validate ${resolvedTemplate.id} link`, error);
        return {
          isValid: false,
          error: `Failed to validate ${resolvedTemplate.label} link`,
        };
      }
    }

    return { isValid: true };
  }

  if (!isValidUrl(trimmedLink)) {
    return { isValid: false, error: "Invalid URL format" };
  }

  if (
    protocol &&
    !allowedProtocols.includes(protocol) &&
    !isDeepLink(trimmedLink)
  ) {
    return {
      isValid: false,
      error: `Protocol ${protocol}:// is not allowed`,
    };
  }

  return { isValid: true };
};

export const validateLinks = (
  links: Array<{ link: string; label: string; fallbackLink?: string }>,
  templates: CommunicationTemplate[] = COMMUNICATION_TEMPLATES,
  allowedProtocols: string[] = ["https", "http", ...DEEP_LINK_PROTOCOLS]
): Array<{ index: number; field: string; error: string }> => {
  const errors: Array<{ index: number; field: string; error: string }> = [];

  links.forEach((linkObj, index) => {
    if (!linkObj.label || !linkObj.label.trim()) {
      errors.push({ index, field: "label", error: "Title is required" });
    }

    const protocol = extractProtocol(linkObj.link);
    const template = protocol
      ? findTemplateByProtocol(protocol, templates)
      : undefined;

    const linkValidation = validateLink(
      linkObj.link,
      template,
      allowedProtocols,
      templates
    );
    if (!linkValidation.isValid) {
      errors.push({
        index,
        field: "link",
        error: linkValidation.error || "Invalid link",
      });
    }

    if (linkObj.fallbackLink && linkObj.fallbackLink.trim()) {
      const fallbackValidation = validateLink(
        linkObj.fallbackLink,
        undefined,
        ["https", "http"],
        templates
      );
      if (!fallbackValidation.isValid) {
        errors.push({
          index,
          field: "fallbackLink",
          error: "Invalid fallback URL",
        });
      }
    }
  });

  return errors;
};
